import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLang } from "../../../shared/i18n/useLang";
import { useToast, ConfirmModal } from "../../../shared/components/SharedUI";
import { overlayClose } from "../../../shared/utils/overlayClose";
import {
  getTickets, getTicket, responderTicket, actualizarTicket, deleteTicket,
  getCategoriasTicket, crearCategoriaTicket, actualizarCategoriaTicket, deleteCategoriaTicket,
} from "../services/portalService";
import {
  EstadoBadge, TicketHilo, SelectorAdjuntos, fechaHora, acento,
  ESTADOS, ICONO_ESTADO, useEstadoLabel,
} from "../components/TicketPartes";
import styles from "../components/tickets.module.css";

/**
 * /portal/admin/tickets — la administración ve los tickets que abrieron los
 * residentes, los responde en el mismo hilo y los va moviendo de estado.
 *
 * Las categorías se administran desde esta misma pantalla (botón Categorías)
 * para no agregar otra opción al menú: son una lista corta que se toca poco.
 */

/** Cuántos tickets hay de cada estado, para las fichas de arriba. */
function contarPorEstado(lista) {
  const conteo = {};
  lista.forEach((t) => { conteo[t.estado] = (conteo[t.estado] || 0) + 1; });
  return conteo;
}

export default function AdminTickets() {
  const L = useLang();
  const en = L("es", "en") === "en";
  const label = useEstadoLabel();
  const { showToast, toastEl } = useToast();

  const [tickets, setTickets]       = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [cargando, setCargando]     = useState(true);

  const [estado, setEstado]       = useState("");
  const [categoria, setCategoria] = useState("");
  const [buscar, setBuscar]       = useState("");

  const [abierto, setAbierto]   = useState(null);   // ticket con su hilo
  const [verCategorias, setVerCategorias] = useState(false);
  const [porBorrar, setPorBorrar] = useState(null);
  const [resumen, setResumen]     = useState({});

  // Respuesta y cambio de estado dentro del hilo
  const [respuesta, setRespuesta] = useState("");
  const [adjuntos, setAdjuntos]   = useState([]);
  const [guardando, setGuardando] = useState(false);

  // `useToast` arma un `showToast` nuevo en cada render, así que no puede ir
  // como dependencia de los cargadores: el efecto que los llama se disparaba
  // en cada render y la pantalla se quedaba pidiendo la lista sin parar. En
  // una ref el aviso sigue siendo el último y la identidad no cambia.
  const avisar = useRef(showToast);
  avisar.current = showToast;

  const hayFiltro = !!(estado || categoria || buscar);

  const cargar = useCallback(() => {
    // El filtro de estado y de categoría se aplican en el servidor; el texto
    // también, porque busca dentro de los mensajes del hilo.
    getTickets({ estado, categoria, buscar })
      .then((d) => {
        const lista = d.results || d;
        setTickets(lista);
        if (!hayFiltro) setResumen(contarPorEstado(lista));
      })
      .catch((e) => avisar.current(e.message, false))
      .finally(() => setCargando(false));
    // Con un filtro puesto la lista ya viene recortada, así que los totales de
    // cada estado se piden aparte: si no, la ficha del estado filtrado sería
    // el único número distinto de cero.
    if (hayFiltro) {
      getTickets()
        .then((d) => setResumen(contarPorEstado(d.results || d)))
        .catch(() => {});
    }
  }, [estado, categoria, buscar, hayFiltro]);

  useEffect(() => {
    const id = setTimeout(cargar, buscar ? 350 : 0);
    return () => clearTimeout(id);
  }, [cargar, buscar]);

  const cargarCategorias = useCallback(() => {
    getCategoriasTicket().then(setCategorias).catch((e) => avisar.current(e.message, false));
  }, []);

  useEffect(() => { cargarCategorias(); }, [cargarCategorias]);

  // Un ticket nuevo o una respuesta del residente llega por la campanita.
  useEffect(() => {
    function onCambio(e) {
      if (e.detail?.tipo === "TICKET") cargar();
    }
    window.addEventListener("portal:data-changed", onCambio);
    return () => window.removeEventListener("portal:data-changed", onCambio);
  }, [cargar]);

  const sinLeer = useMemo(() => tickets.filter((t) => t.sin_leer).length, [tickets]);

  async function abrirHilo(id) {
    try {
      setRespuesta(""); setAdjuntos([]);
      setAbierto(await getTicket(id));
      setTickets((prev) => prev.map((t) => (t.id === id ? { ...t, sin_leer: false } : t)));
    } catch (e) {
      showToast(e.message, false);
    }
  }

  async function responder(e) {
    e.preventDefault();
    setGuardando(true);
    try {
      const fd = new FormData();
      fd.append("cuerpo", respuesta);
      adjuntos.forEach((a) => fd.append("archivos", a));
      await responderTicket(abierto.id, fd);
      setRespuesta(""); setAdjuntos([]);
      setAbierto(await getTicket(abierto.id));
      cargar();
      showToast(L("Respuesta enviada.", "Reply sent."), true);
    } catch (err) {
      showToast(err.message, false);
    } finally {
      setGuardando(false);
    }
  }

  async function cambiarEstado(nuevo) {
    if (nuevo === abierto.estado) return;
    setGuardando(true);
    try {
      // El comentario que esté escrito se manda junto con el cambio: queda
      // como un solo mensaje del hilo en vez de dos.
      const payload = { estado: nuevo };
      if (respuesta.trim()) payload.comentario = respuesta.trim();
      await actualizarTicket(abierto.id, payload);
      setRespuesta("");
      setAbierto(await getTicket(abierto.id));
      cargar();
      showToast(L(`Ticket marcado como ${label(nuevo)}.`, `Ticket marked as ${label(nuevo)}.`), true);
    } catch (err) {
      showToast(err.message, false);
    } finally {
      setGuardando(false);
    }
  }

  async function cambiarCategoria(valor) {
    setGuardando(true);
    try {
      await actualizarTicket(abierto.id, { categoria: valor || null });
      setAbierto(await getTicket(abierto.id));
      cargar();
    } catch (err) {
      showToast(err.message, false);
    } finally {
      setGuardando(false);
    }
  }

  async function borrar() {
    try {
      await deleteTicket(porBorrar.id);
      setPorBorrar(null);
      setAbierto(null);
      cargar();
      showToast(L("Ticket eliminado.", "Ticket deleted."), true);
    } catch (err) {
      showToast(err.message, false);
    }
  }

  return (
    <>
      {toastEl}
      <div className={styles.page}>
        <div className={styles.hero}>
          <div className={styles.heroTexto}>
            <span className={styles.heroIcono}>
              <i className="fa-solid fa-headset" />
            </span>
            <div>
              <h1 className={styles.heroTitulo}>{L("Tickets", "Tickets")}</h1>
              <p className={styles.heroSub}>
                {L("Reclamos, consultas y reportes que abrieron los residentes desde el portal.",
                   "Claims, questions and reports opened by residents from the portal.")}
              </p>
            </div>
          </div>
          <div className={styles.heroAcciones}>
            {sinLeer > 0 && (
              <span className={styles.heroPastilla}>
                <i className="fa-solid fa-envelope" />
                {L(`${sinLeer} sin leer`, `${sinLeer} unread`)}
              </span>
            )}
            <button type="button" className={styles.btn} onClick={() => setVerCategorias(true)}>
              <i className="fa-solid fa-tags" /> {L("Categorías", "Categories")}
            </button>
          </div>
        </div>

        <div className={styles.fichas}>
          {ESTADOS.map((e) => (
            <button
              key={e}
              type="button"
              className={`${styles.ficha} ${acento(e)} ${estado === e ? styles.fichaActiva : ""}`}
              onClick={() => setEstado(estado === e ? "" : e)}
              title={estado === e
                ? L("Quitar el filtro", "Clear the filter")
                : L(`Ver solo los de estado ${label(e)}`, `Show only ${label(e)}`)}
            >
              <i className={`fa-solid ${ICONO_ESTADO[e]} ${styles.fichaDeco}`} />
              <span className={styles.fichaIcono}>
                <i className={`fa-solid ${ICONO_ESTADO[e]}`} />
              </span>
              <span>
                <span className={styles.fichaValor}>{resumen[e] || 0}</span>
                <span className={styles.fichaLabel}>{label(e)}</span>
              </span>
            </button>
          ))}
        </div>

        <div className={`${styles.filtros} ${styles.filtrosCaja}`}>
          <div className={styles.filtro}>
            <label className={styles.filtroLabel} htmlFor="f-categoria">
              {L("Categoría", "Category")}
            </label>
            <select
              id="f-categoria"
              className="form-control"
              value={categoria}
              onChange={(ev) => setCategoria(ev.target.value)}
            >
              <option value="">{L("Todas", "All")}</option>
              {categorias.map((c) => (
                <option key={c.id} value={c.id}>{c.nombre}</option>
              ))}
            </select>
          </div>
          <div className={`${styles.filtro} ${styles.filtroAncho}`}>
            <label className={styles.filtroLabel} htmlFor="f-buscar">
              {L("Buscar", "Search")}
            </label>
            <div className={styles.conLupa}>
              <i className="fa-solid fa-magnifying-glass" />
              <input
                id="f-buscar"
                className="form-control"
                value={buscar}
                onChange={(ev) => setBuscar(ev.target.value)}
                placeholder={L("Asunto, filial o texto del hilo", "Subject, unit or thread text")}
              />
            </div>
          </div>
          {(estado || categoria || buscar) && (
            <button
              type="button"
              className={styles.btn}
              onClick={() => { setEstado(""); setCategoria(""); setBuscar(""); }}
            >
              <i className="fa-solid fa-filter-circle-xmark" /> {L("Limpiar", "Clear")}
            </button>
          )}
        </div>

        {cargando ? (
          <div className={styles.vacio}><p>{L("Cargando...", "Loading...")}</p></div>
        ) : tickets.length === 0 ? (
          <div className={styles.vacio}>
            <i className={`fa-solid fa-inbox ${styles.vacioIcono}`} />
            <p>
              {estado || categoria || buscar
                ? L("Ningún ticket calza con el filtro.", "No tickets match the filter.")
                : L("Todavía no hay tickets.", "No tickets yet.")}
            </p>
          </div>
        ) : (
          <div className={styles.tablaCaja}>
            <div className={styles.tablaScroll}>
              <table className={styles.tabla}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{L("Filial", "Unit")}</th>
                    <th>{L("Asunto", "Subject")}</th>
                    <th>{L("Categoría", "Category")}</th>
                    <th>{L("Estado", "Status")}</th>
                    <th>{L("Último mensaje", "Last message")}</th>
                    <th aria-hidden="true" />
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((t) => (
                    <tr key={t.id} className={acento(t.estado)} onClick={() => abrirHilo(t.id)}>
                      <td className={styles.celdaId}>{t.id}</td>
                      <td>
                        <span className={styles.tarjetaFila}>
                          {t.sin_leer && <span className={styles.punto} />}
                          {t.filial_nombre}
                        </span>
                      </td>
                      <td className={styles.celdaAsunto}>{t.asunto}</td>
                      <td>
                        {t.categoria_nombre ? (
                          <span className={styles.chip}>
                            <i className="fa-solid fa-tag" /> {t.categoria_nombre}
                          </span>
                        ) : (
                          <span className={styles.celdaTenue}>-</span>
                        )}
                      </td>
                      <td><EstadoBadge estado={t.estado} /></td>
                      <td className={styles.celdaTenue}>
                        <span className={styles.conIcono}>
                          <i className="fa-regular fa-clock" />
                          {fechaHora(t.ultimo_mensaje_at || t.created_at, en)}
                        </span>
                      </td>
                      <td className={styles.celdaFlecha}>
                        <i className="fa-solid fa-chevron-right" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {abierto && (
        <div className={styles.overlay} {...overlayClose(() => setAbierto(null))}>
          <div className={`${styles.modal} ${styles.modalAncho}`}>
            <div className={styles.modalCabeza}>
              <div>
                <h2 className={styles.modalTitulo}>#{abierto.id} {abierto.asunto}</h2>
                <p className={styles.modalMeta}>
                  <EstadoBadge estado={abierto.estado} />
                  <span className={styles.separador}>·</span>
                  <span>{L("Filial", "Unit")} {abierto.filial_nombre}</span>
                  <span className={styles.separador}>·</span>
                  <span>{abierto.abierto_por}</span>
                  <span className={styles.separador}>·</span>
                  <span>{fechaHora(abierto.created_at, en)}</span>
                </p>
              </div>
              <button
                type="button"
                className={styles.btnIcono}
                onClick={() => setAbierto(null)}
                aria-label={L("Cerrar", "Close")}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            <div className={styles.modalCuerpo}>
              <div className={styles.filtros}>
                <div className={styles.filtro}>
                  <label className={styles.filtroLabel} htmlFor="d-categoria">
                    {L("Categoría", "Category")}
                  </label>
                  <select
                    id="d-categoria"
                    className="form-control"
                    value={abierto.categoria || ""}
                    disabled={guardando}
                    onChange={(ev) => cambiarCategoria(ev.target.value)}
                  >
                    <option value="">{L("Sin categoría", "No category")}</option>
                    {categorias.map((c) => (
                      <option key={c.id} value={c.id}>{c.nombre}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.filtro}>
                  <label className={styles.filtroLabel} htmlFor="d-estado">
                    {L("Estado", "Status")}
                  </label>
                  <select
                    id="d-estado"
                    className="form-control"
                    value={abierto.estado}
                    disabled={guardando}
                    onChange={(ev) => cambiarEstado(ev.target.value)}
                  >
                    {ESTADOS.map((e) => (
                      <option key={e} value={e}>{label(e)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <TicketHilo mensajes={abierto.mensajes} esAdmin />

              {abierto.estado === "CERRADO" ? (
                <div className={styles.cerradoAviso}>
                  <p>
                    {L("El ticket está cerrado. Cambiale el estado arriba para volver a escribir en el hilo.",
                       "This ticket is closed. Change its status above to write in the thread again.")}
                  </p>
                </div>
              ) : (
                <form className={styles.composer} onSubmit={responder}>
                  <label className={styles.etiqueta} htmlFor="d-respuesta">
                    {L("Responder al residente", "Reply to the resident")}
                  </label>
                  <textarea
                    id="d-respuesta"
                    className={`form-control ${styles.areaTexto}`}
                    value={respuesta}
                    onChange={(ev) => setRespuesta(ev.target.value)}
                    rows={3}
                    placeholder={L(
                      "Escribí la respuesta. Si cambiás el estado con texto escrito, va junto con el cambio.",
                      "Write the reply. If you change the status with text written, it goes with the change.",
                    )}
                  />
                  <div className={styles.composerFila}>
                    <SelectorAdjuntos archivos={adjuntos} onChange={setAdjuntos} disabled={guardando} />
                    <button
                      type="submit"
                      className={`${styles.btn} ${styles.btnPrimario}`}
                      disabled={guardando || (!respuesta.trim() && !adjuntos.length)}
                    >
                      <i className="fa-solid fa-paper-plane" />
                      {guardando ? L("Enviando...", "Sending...") : L("Enviar", "Send")}
                    </button>
                  </div>
                </form>
              )}
            </div>

            <div className={styles.modalPie}>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnPeligro}`}
                onClick={() => setPorBorrar(abierto)}
              >
                <i className="fa-solid fa-trash" /> {L("Eliminar", "Delete")}
              </button>
              <button type="button" className={styles.btn} onClick={() => setAbierto(null)}>
                {L("Cerrar", "Close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {verCategorias && (
        <CategoriasModal
          categorias={categorias}
          onCerrar={() => { setVerCategorias(false); cargar(); }}
          onCambio={cargarCategorias}
          showToast={showToast}
        />
      )}

      <ConfirmModal
        open={!!porBorrar}
        name={porBorrar ? `#${porBorrar.id} ${porBorrar.asunto}` : ""}
        message={L(
          "Se borra el ticket con todo su hilo y sus adjuntos. No se puede deshacer.",
          "The ticket, its whole thread and its attachments are deleted. This cannot be undone.",
        )}
        onConfirm={borrar}
        onCancel={() => setPorBorrar(null)}
      />
    </>
  );
}

/** Alta, nombre, orden y baja de las categorías del condominio. */
function CategoriasModal({ categorias, onCerrar, onCambio, showToast }) {
  const L = useLang();
  const [nueva, setNueva]   = useState("");
  const [editando, setEditando] = useState(null);   // {id, nombre}
  const [ocupado, setOcupado]   = useState(false);

  async function correr(accion) {
    setOcupado(true);
    try {
      await accion();
      onCambio();
    } catch (e) {
      showToast(e.message, false);
    } finally {
      setOcupado(false);
    }
  }

  function agregar(e) {
    e.preventDefault();
    const nombre = nueva.trim();
    if (!nombre) return;
    correr(async () => {
      await crearCategoriaTicket(nombre);
      setNueva("");
    });
  }

  function guardarNombre() {
    const nombre = (editando?.nombre || "").trim();
    if (!nombre) { setEditando(null); return; }
    const id = editando.id;
    correr(async () => {
      await actualizarCategoriaTicket(id, { nombre });
      setEditando(null);
    });
  }

  return (
    <div className={styles.overlay} {...overlayClose(onCerrar)}>
      <div className={styles.modal}>
        <div className={styles.modalCabeza}>
          <div>
            <h2 className={styles.modalTitulo}>{L("Categorías de tickets", "Ticket categories")}</h2>
            <p className={styles.modalMeta}>
              {L("Son las que el residente elige al abrir un ticket.",
                 "These are what the resident picks when opening a ticket.")}
            </p>
          </div>
          <button
            type="button"
            className={styles.btnIcono}
            onClick={onCerrar}
            aria-label={L("Cerrar", "Close")}
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className={styles.modalCuerpo}>
          <div className={styles.categorias}>
            {categorias.map((c) => (
              <div key={c.id} className={`${styles.categoria} ${c.activa ? "" : styles.categoriaInactiva}`}>
                {editando?.id === c.id ? (
                  <input
                    className="form-control"
                    autoFocus
                    value={editando.nombre}
                    maxLength={80}
                    onChange={(e) => setEditando({ ...editando, nombre: e.target.value })}
                    onBlur={guardarNombre}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") guardarNombre();
                      if (e.key === "Escape") setEditando(null);
                    }}
                  />
                ) : (
                  <span className={styles.categoriaNombre}>{c.nombre}</span>
                )}
                {!c.activa && (
                  <span className={styles.categoriaEtiqueta}>{L("Inactiva", "Inactive")}</span>
                )}
                <button
                  type="button"
                  className={styles.btnIcono}
                  disabled={ocupado}
                  title={L("Cambiar el nombre", "Rename")}
                  onClick={() => setEditando({ id: c.id, nombre: c.nombre })}
                >
                  <i className="fa-solid fa-pen" />
                </button>
                <button
                  type="button"
                  className={styles.btnIcono}
                  disabled={ocupado}
                  title={c.activa
                    ? L("Desactivar: deja de ofrecerse al residente", "Deactivate: stop offering it to residents")
                    : L("Activar", "Activate")}
                  onClick={() => correr(() => actualizarCategoriaTicket(c.id, { activa: !c.activa }))}
                >
                  <i className={`fa-solid ${c.activa ? "fa-eye" : "fa-eye-slash"}`} />
                </button>
                <button
                  type="button"
                  className={styles.btnIcono}
                  disabled={ocupado || c.en_uso}
                  title={c.en_uso
                    ? L("Ya tiene tickets: desactivala en vez de borrarla", "It already has tickets: deactivate instead")
                    : L("Borrar", "Delete")}
                  onClick={() => correr(() => deleteCategoriaTicket(c.id))}
                >
                  <i className="fa-solid fa-trash" />
                </button>
              </div>
            ))}
          </div>

          <form className={styles.nuevaCategoria} onSubmit={agregar}>
            <input
              className="form-control"
              value={nueva}
              maxLength={80}
              onChange={(e) => setNueva(e.target.value)}
              placeholder={L("Nombre de la categoría nueva", "New category name")}
            />
            <button
              type="submit"
              className={`${styles.btn} ${styles.btnPrimario}`}
              disabled={ocupado || !nueva.trim()}
            >
              <i className="fa-solid fa-plus" /> {L("Agregar", "Add")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
